/**
 * AdminAccessPanel — admin-only panel for managing access requests & invite codes.
 *
 * Tabs:
 *   - Pending requests (with approve/reject)
 *   - All requests (history)
 *   - Invite codes (active, with revoke + create)
 */
import React, { useEffect, useState } from 'react';
import { AuthService, AccessRequest, InviteCode } from '../services/authService';

type Tab = 'pending' | 'all' | 'codes';

export default function AdminAccessPanel() {
  const [tab, setTab] = useState<Tab>('pending');
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [codes, setCodes] = useState<InviteCode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [lastApprovedCode, setLastApprovedCode] = useState<{ email: string; code: string; emailSent: boolean } | null>(null);

  // New invite form
  const [newEmail, setNewEmail] = useState('');
  const [newMaxUses, setNewMaxUses] = useState(1);
  const [newExpiresInDays, setNewExpiresInDays] = useState(30);
  const [newNote, setNewNote] = useState('');

  const refresh = async () => {
    setLoading(true);
    setError('');
    try {
      if (tab === 'pending') {
        setRequests(await AuthService.listAccessRequests('PENDING'));
      } else if (tab === 'all') {
        setRequests(await AuthService.listAccessRequests());
      } else {
        setCodes(await AuthService.listInviteCodes());
      }
    } catch (e: any) {
      setError(e?.message || 'Laden mislukt');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void refresh(); }, [tab]);

  const handleApprove = async (req: AccessRequest) => {
    const note = window.prompt(`Optioneel bericht voor ${req.email}:`, 'Welkom bij de pilot!') ?? undefined;
    setActionLoading(req.id);
    setError('');
    try {
      const r = await AuthService.approveAccessRequest(req.id, {
        note: note || undefined,
        expiresInDays: 30,
      });
      setLastApprovedCode({ email: req.email, code: r.inviteCode, emailSent: r.emailSent });
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Goedkeuren mislukt');
    } finally {
      setActionLoading(null);
    }
  };

  const handleReject = async (req: AccessRequest) => {
    if (!window.confirm(`Weiger de aanvraag van ${req.email}?`)) return;
    setActionLoading(req.id);
    setError('');
    try {
      await AuthService.rejectAccessRequest(req.id);
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Afwijzen mislukt');
    } finally {
      setActionLoading(null);
    }
  };

  const handleRevoke = async (code: InviteCode) => {
    if (!window.confirm(`Code ${code.code} intrekken?`)) return;
    setActionLoading(code.id);
    try {
      await AuthService.revokeInviteCode(code.id);
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Intrekken mislukt');
    } finally {
      setActionLoading(null);
    }
  };

  const handleCreateCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setActionLoading('create');
    setError('');
    try {
      const inv = await AuthService.createInviteCode({
        email: newEmail.trim() || undefined,
        maxUses: newMaxUses,
        expiresInDays: newExpiresInDays || undefined,
        note: newNote.trim() || undefined,
      });
      setLastApprovedCode({ email: inv.email || 'anyone', code: inv.code, emailSent: false });
      setNewEmail(''); setNewNote(''); setNewMaxUses(1); setNewExpiresInDays(30);
      await refresh();
    } catch (e: any) {
      setError(e?.message || 'Aanmaken mislukt');
    } finally {
      setActionLoading(null);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text).then(() => {
      setError('✓ Gekopieerd naar klembord');
      setTimeout(() => setError(''), 2000);
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3 border-b border-white/10 pb-3">
        {(['pending', 'all', 'codes'] as Tab[]).map((t) => (
          <button
            key={t}
            onClick={() => { setTab(t); setLastApprovedCode(null); }}
            className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-[0.18em] transition ${
              tab === t
                ? 'bg-indigo-500/20 text-indigo-200 border border-indigo-400/30'
                : 'text-slate-400 hover:text-white'
            }`}
          >
            {t === 'pending' ? 'Openstaand' : t === 'all' ? 'Alle aanvragen' : 'Codes'}
          </button>
        ))}
      </div>

      {lastApprovedCode && (
        <div className="rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-4">
          <p className="text-[10px] font-black uppercase tracking-[0.3em] text-emerald-300">Code aangemaakt</p>
          <div className="mt-2 flex items-center gap-3">
            <code className="flex-1 font-mono text-sm text-white bg-black/30 px-3 py-2 rounded-lg">
              {lastApprovedCode.code}
            </code>
            <button onClick={() => copyToClipboard(lastApprovedCode.code)} className="rounded-lg bg-white/10 px-3 py-2 text-xs text-white hover:bg-white/20">
              Kopieer
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-300">
            Voor: <span className="font-mono">{lastApprovedCode.email}</span>
            {lastApprovedCode.emailSent ? ' (✓ gemaild)' : ' (⚠️ handmatig mailen — geen SendGrid key)'}
          </p>
        </div>
      )}

      {error && (
        <div className="rounded-2xl border border-rose-500/30 bg-rose-500/10 p-3 text-sm text-rose-200">{error}</div>
      )}

      {loading && <p className="text-sm text-slate-400">Laden…</p>}

      {tab === 'codes' && (
        <div className="space-y-6">
          <form onSubmit={handleCreateCode} className="rounded-2xl border border-white/10 bg-white/5 p-5 space-y-3">
            <p className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400">Nieuwe code aanmaken</p>
            <div className="grid grid-cols-2 gap-3">
              <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="E-mail (leeg = iedereen)"
                className="rounded-xl border border-white/10 bg-[#0b1120] px-3 py-2 text-sm text-white placeholder-slate-500" />
              <input type="number" min={1} max={100} value={newMaxUses} onChange={(e) => setNewMaxUses(parseInt(e.target.value) || 1)} placeholder="Max uses"
                className="rounded-xl border border-white/10 bg-[#0b1120] px-3 py-2 text-sm text-white" />
              <input type="number" min={1} max={365} value={newExpiresInDays} onChange={(e) => setNewExpiresInDays(parseInt(e.target.value) || 30)} placeholder="Geldig (dagen)"
                className="rounded-xl border border-white/10 bg-[#0b1120] px-3 py-2 text-sm text-white" />
              <input type="text" value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Notitie (intern)"
                className="rounded-xl border border-white/10 bg-[#0b1120] px-3 py-2 text-sm text-white" />
            </div>
            <button type="submit" disabled={actionLoading === 'create'}
              className="rounded-xl bg-gradient-to-r from-indigo-500 to-violet-500 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-white hover:from-indigo-400 disabled:opacity-50">
              {actionLoading === 'create' ? 'Bezig…' : '+ Code aanmaken'}
            </button>
          </form>

          <div className="space-y-2">
            {codes.length === 0 && !loading && <p className="text-sm text-slate-500">Geen codes.</p>}
            {codes.map((c) => (
              <div key={c.id} className="rounded-xl border border-white/10 bg-white/5 p-4 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <code className="font-mono text-sm text-white">{c.code}</code>
                  <p className="mt-1 text-xs text-slate-400">
                    {c.email || '🌐 iedereen'} · {c.usedCount}/{c.maxUses} gebruikt
                    {c.expiresAt && ` · verloopt ${new Date(c.expiresAt).toLocaleDateString('nl-NL')}`}
                    {c.note && ` · ${c.note}`}
                  </p>
                </div>
                <button onClick={() => copyToClipboard(c.code)} className="rounded-lg bg-white/10 px-3 py-1 text-xs text-white hover:bg-white/20">Kopieer</button>
                {c.usedCount < c.maxUses && (
                  <button onClick={() => handleRevoke(c)} disabled={actionLoading === c.id}
                    className="rounded-lg bg-rose-500/20 px-3 py-1 text-xs text-rose-200 hover:bg-rose-500/30 disabled:opacity-50">
                    Intrekken
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {(tab === 'pending' || tab === 'all') && (
        <div className="space-y-3">
          {requests.length === 0 && !loading && <p className="text-sm text-slate-500">Geen aanvragen.</p>}
          {requests.map((r) => (
            <div key={r.id} className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-3">
                    <p className="text-base font-bold text-white">{r.name}</p>
                    <span className={`text-[10px] font-black uppercase tracking-[0.25em] px-2 py-0.5 rounded ${
                      r.status === 'PENDING' ? 'bg-amber-500/20 text-amber-300' :
                      r.status === 'APPROVED' ? 'bg-emerald-500/20 text-emerald-300' :
                      'bg-rose-500/20 text-rose-300'
                    }`}>
                      {r.status}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-slate-300">
                    {r.email}{r.company && ` · ${r.company}`}
                  </p>
                  {r.message && <p className="mt-2 text-sm text-slate-400 italic">"{r.message}"</p>}
                  <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    {new Date(r.createdAt).toLocaleString('nl-NL')}
                  </p>
                </div>
                {r.status === 'PENDING' && (
                  <div className="flex gap-2 flex-shrink-0">
                    <button onClick={() => handleReject(r)} disabled={actionLoading === r.id}
                      className="rounded-xl border border-rose-500/30 bg-rose-500/10 px-3 py-2 text-xs font-bold text-rose-200 hover:bg-rose-500/20 disabled:opacity-50">
                      Weiger
                    </button>
                    <button onClick={() => handleApprove(r)} disabled={actionLoading === r.id}
                      className="rounded-xl bg-gradient-to-r from-emerald-500 to-emerald-600 px-3 py-2 text-xs font-bold text-white hover:from-emerald-400 disabled:opacity-50">
                      {actionLoading === r.id ? '…' : '✓ Goedkeuren'}
                    </button>
                  </div>
                )}
              </div>
              {r.inviteCodes && r.inviteCodes.length > 0 && (
                <div className="mt-3 border-t border-white/10 pt-3">
                  <p className="text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">Gegenereerde code</p>
                  <code className="mt-1 block font-mono text-sm text-emerald-300">{r.inviteCodes[0].code}</code>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
